import { useEffect, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import jalali from "jalali-plugin-dayjs";

dayjs.extend(jalali);

type Props = {
    value?: Dayjs | null;
    onChange?: (d: Dayjs | null) => void;
    placeholder?: string;
};

export default function JalaliDatePicker({
    value = null,
    onChange,
    placeholder = "انتخاب تاریخ",
}: Props) {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<Dayjs | null>(value);
    const [viewDate, setViewDate] = useState<Dayjs>(
        value ?? dayjs().calendar("jalali")
    );

    const ref = useRef<HTMLDivElement | null>(null);

    const months = [
        "فروردین",
        "اردیبهشت",
        "خرداد",
        "تیر",
        "مرداد",
        "شهریور",
        "مهر",
        "آبان",
        "آذر",
        "دی",
        "بهمن",
        "اسفند",
    ];

    useEffect(() => {
        function clickOutside(e: MouseEvent) {
            if (!ref.current?.contains(e.target as Node)) setOpen(false);
        }
        document.addEventListener("mousedown", clickOutside);
        return () => document.removeEventListener("mousedown", clickOutside);
    }, []);

    useEffect(() => {
        if (value) {
            setSelected(value);
            setViewDate(value);
        }
    }, [value]);

    const handleSelect = (day: number) => {
        const picked = viewDate.date(day);

        setSelected(picked);
        onChange?.(picked);
        setOpen(false);
    };

    const daysInMonth = viewDate.daysInMonth();

    const firstDay = (viewDate.startOf("month").day() + 1) % 7;

    const weeks: (number | null)[][] = [];
    const cells: (number | null)[] = [];

    for (let i = 0; i < firstDay; i++) {
        cells.push(null);
    }

    for (let d = 1; d <= daysInMonth; d++) {
        cells.push(d);
    }

    for (let i = 0; i < cells.length; i += 7) {
        weeks.push(cells.slice(i, i + 7));
    }


    const today = dayjs().calendar("jalali");

    const years = Array.from({ length: 100 }, (_, i) => today.year() - i);

    return (
        <div className="relative" ref={ref}>
            <div
                className="w-full h-[50px] rounded-xl px-4 text-right bg-gradient-to-br from-[#3a3a3a] to-[#2c2c2c]
                 text-white outline-none border border-transparent focus:border-primary/40 flex flex-col justify-center"
                onClick={() => setOpen(o => !o)}
            >
                <span className="text-gray-200">
                    {selected
                        ? selected.format("YYYY/MM/DD")
                        : placeholder}
                </span>
            </div>

            {open && (
                <div className="absolute bottom-0  overflow-y-hidden z-50 mt-2 bg-white shadow-lg rounded-xl p-4 w-80">
                    <div className="flex items-center justify-between mb-3">
                        <select
                            value={viewDate.month()}
                            onChange={e =>
                                setViewDate(viewDate.month(Number(e.target.value)))
                            }
                            className="border rounded-lg px-2 py-1 text-sm bg-gray-50 text-gray-700">
                            {months.map((m, i) => (
                                <option key={i} value={i}>
                                    {m}
                                </option>
                            ))}
                        </select>

                        <select
                            value={viewDate.year()}
                            onChange={e =>
                                setViewDate(viewDate.year(Number(e.target.value)))
                            }
                            className="border rounded-lg px-2 py-1 text-sm bg-gray-50 text-gray-700">
                            {years.map(y => (
                                <option key={y} value={y}>
                                    {y}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-7 gap-1 text-xs text-center text-gray-500 mb-1">
                        <div>ش</div>
                        <div>ی</div>
                        <div>د</div>
                        <div>س</div>
                        <div>چ</div>
                        <div>پ</div>
                        <div>ج</div>
                    </div>

                    <div className="grid grid-cols-7 gap-1">
                        {weeks.map((row, ri) =>
                            row.map((d, ci) => {
                                if (!d)
                                    return <div key={`${ri}-${ci}`} className="h-10" />;

                                const thisDate = viewDate.date(d);
                                const isSelected =
                                    selected && thisDate.isSame(selected, "day");
                                const isToday = thisDate.isSame(today, "day");

                                return (
                                    <button
                                        key={`${ri}-${ci}`}
                                        onClick={() => handleSelect(d)}
                                        className={`h-10 rounded-md text-sm flex items-center justify-center transition
                                                  ${isSelected ? "bg-blue-600 text-white" : "hover:bg-gray-600 text-gray-800"}
                                                  ${isToday ? "ring-1 ring-indigo-200" : ""}
                                                  `}>
                                        {d}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
